import { useState, useEffect } from 'react';
import { Grid, Typography, Button, TextField, MenuItem, Skeleton, Select, InputLabel, FormControl } from '@mui/material';
import PropTypes from 'prop-types';
import vendorService from 'api/vendorService';
import { gridSpacing } from 'store/constant';
import { useAllMarketCategories } from 'hooks/useMarketCategory';
import { toast } from 'react-toastify';

const VendorBusiness = ({ data, isLoading }) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [marketCategoryId, setMarketCategoryId] = useState('');
    const [loading, setLoading] = useState(false);

    const { data: marketCategories, isLoading: marketCategoriesLoading, error: marketCategoriesError } = useAllMarketCategories();

    // 🧩 Initialize fields when data changes
    useEffect(() => {
        if (data?.vendor) {
            setName(data.vendor.name || '');
            setEmail(data.vendor.email || '');
            setPhoneNumber(data.vendor.phoneNumber || '');
            // ✅ Use the ID, not the name
            setMarketCategoryId(data.vendor.marketCategory?._id || data.vendor.marketCategoryId || '');
        }
    }, [data]);

    // 💾 Handle submit
    const handleSubmit = async () => {
        try {
            setLoading(true);
            const payload = { name, email, phoneNumber, marketCategoryId };
            await vendorService.updateVendor(payload);
            toast.success('Business info updated successfully!');
        } catch (error) {
            toast.error('Failed to update business info');
        } finally {
            setLoading(false);
        }
    };

    // 🦴 Show skeletons while loading vendor data
    if (isLoading) {
        return (
            <Grid container spacing={gridSpacing} paddingX={5} pt={3}>
                <Grid item md={5}>
                    <Skeleton variant="text" width={200} height={40} />
                    <Skeleton variant="text" width={300} height={20} />
                </Grid>
                <Grid item md={7}>
                    {[1, 2, 3, 4].map((i) => (
                        <Skeleton key={i} variant="rectangular" height={56} sx={{ mb: 2, borderRadius: 2 }} />
                    ))}
                    <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 2 }} />
                </Grid>
            </Grid>
        );
    }

    // 🧾 Render form when data is ready
    return (
        <Grid container spacing={gridSpacing} paddingX={5} pt={3}>
            <Grid item md={5}>
                <Typography fontWeight="bold" fontSize={20}>
                    Business Information
                </Typography>
                <Typography color="grey" fontSize={14}>
                    Update your business details below.
                </Typography>
            </Grid>

            <Grid item md={2}>
                -
            </Grid>

            <Grid item md={5}>
                <TextField fullWidth label="Business Name" value={name} onChange={(e) => setName(e.target.value)} sx={{ mb: 2 }} />

                <TextField fullWidth label="Business Email" value={email} onChange={(e) => setEmail(e.target.value)} sx={{ mb: 2 }} />

                <TextField
                    fullWidth
                    label="Business Phone Number"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    sx={{ mb: 2 }}
                />

                {/* if categories still loading */}
                {marketCategoriesLoading ? (
                    <Skeleton variant="rectangular" height={56} sx={{ mb: 2, borderRadius: 2 }} />
                ) : marketCategoriesError ? (
                    <Typography color="error" sx={{ mb: 2 }}>
                        Failed to load market categories
                    </Typography>
                ) : (
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel id="market-category-label">Market Category</InputLabel>
                        <Select
                            labelId="market-category-label"
                            value={marketCategoryId}
                            label="Market Category"
                            onChange={(e) => setMarketCategoryId(e.target.value)}
                        >
                            {/* Pre-select the vendor's current category */}
                            {data?.vendor?.marketCategory && (
                                <MenuItem key={data.vendor.marketCategory._id} value={data.vendor.marketCategory._id}>
                                    {data.vendor.marketCategory.name}
                                </MenuItem>
                            )}

                            {/* List of all categories (filter out the one already shown above if it matches) */}
                            {Array.isArray(marketCategories?.data) &&
                                marketCategories.data
                                    .filter((cat) => cat._id !== data?.vendor?.marketCategory?._id)
                                    .map((category) => (
                                        <MenuItem key={category._id} value={category._id}>
                                            {category.name}
                                        </MenuItem>
                                    ))}
                        </Select>
                    </FormControl>
                )}

                <Button variant="contained" color="primary" disabled={loading} onClick={handleSubmit}>
                    {loading ? 'Saving...' : 'Save Changes'}
                </Button>
            </Grid>
        </Grid>
    );
};

VendorBusiness.propTypes = {
    data: PropTypes.shape({
        vendor: PropTypes.shape({
            name: PropTypes.string,
            email: PropTypes.string,
            phoneNumber: PropTypes.string,
            marketCategoryId: PropTypes.string,
            marketCategory: PropTypes.shape({
                _id: PropTypes.string,
                name: PropTypes.string
            })
        })
    }),
    isLoading: PropTypes.bool,
    error: PropTypes.any
};

export default VendorBusiness;
